import { useRef, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Printer, CalendarDays, Info, AlertTriangle, Download, Loader2 } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { track, trackPdfDownload } from "@/lib/track";

/* Dedicated A3-landscape renderer for the crib sheets.
 *
 * Design goals:
 *   - Print target: A3 297×420mm LANDSCAPE. The @page rule below primes the
 *     browser print dialog to the correct size + orientation.
 *   - On-screen: preserve the same information architecture as the print
 *     copy so learners can browse tables on their phone/laptop, then print
 *     when they want a physical crib sheet.
 *   - Density: 3-column masonry layout of small tables + note lists + SVG
 *     dose-vs-weight charts + warning callouts.
 *   - The A3 @page rule is scoped to elements INSIDE .cs-a3 to avoid
 *     bleeding into other pages if this component is embedded elsewhere.
 *
 * Print-visibility isolation uses the existing global `.print-area` class
 * (defined in index.css) which hides everything outside it during print.
 */

const A3_STYLE = `
  @media print {
    @page {
      size: A3 landscape;
      margin: 8mm;
    }
    .cs-a3 {
      font-size: 8.5pt !important;
      line-height: 1.25 !important;
    }
    .cs-a3 h1 { font-size: 15pt !important; }
    .cs-a3 h2 { font-size: 9.5pt !important; }
    .cs-a3 table { font-size: 8pt !important; }
    .cs-a3 th, .cs-a3 td { padding: 2px 4px !important; }
    .cs-a3 .cs-section { break-inside: avoid; page-break-inside: avoid; }
    .cs-a3 .cs-foot { font-size: 7pt !important; }
    .cs-a3 .cs-grid { gap: 6mm !important; }
    .cs-a3 .cs-note-item { font-size: 8pt !important; padding: 2px 0 !important; }
  }
`;

export default function CheatSheetA3({ sheet }) {
  const navigate = useNavigate();
  const areaRef = useRef(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const viewFiredRef = useRef(null);

  // `cheat_sheet_view` on mount — deduped by sheet.id in case the parent
  // route swaps `sheet` prop without unmounting this component.
  useEffect(() => {
    if (!sheet?.id) return;
    if (viewFiredRef.current === sheet.id) return;
    viewFiredRef.current = sheet.id;
    track("cheat_sheet_view", { sheet_id: sheet.id });
  }, [sheet?.id]);
  const Icon = sheet.Icon;
  const reviewed = formatReviewed(sheet.lastReviewed);

  const savePdf = async () => {
    if (pdfBusy) return;
    setPdfBusy(true);
    // Fire the tracking event optimistically — a mid-flow crash below still
    // means "the user clicked download for this sheet", which is what we
    // want to measure. Best-effort; never blocks the PDF flow.
    trackPdfDownload(sheet.id);
    try {
      // Lazy-load the PDF library only when the user clicks — keeps the
      // initial bundle small (html2pdf is ~350 KB).
      const html2pdfMod = await import("html2pdf.js");
      const html2pdf = html2pdfMod.default || html2pdfMod;
      const element = areaRef.current;
      if (!element) return;
      const filename = `${sheet.id}-crib-sheet.pdf`;
      await html2pdf()
        .set({
          margin: 5,
          filename,
          image: { type: "jpeg", quality: 0.95 },
          html2canvas: {
            scale: 2,           // 2× DPI for crisp text/tables at print sizes
            useCORS: true,
            letterRendering: true,
            backgroundColor: "#ffffff",
          },
          jsPDF: { unit: "mm", format: "a3", orientation: "landscape" },
          pagebreak: { mode: ["avoid-all", "css"], avoid: ".cs-section" },
        })
        .from(element)
        .save();
    } catch (e) {
      // Fallback: if the library fails to load or PDF generation errors,
      // fall through to native print so the user still gets a PDF via the
      // browser's "Save as PDF" destination.
      window.print();
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F9] print:bg-white">
      <style>{A3_STYLE}</style>

      {/* Screen-only header — hidden on print */}
      <header className="border-b border-slate-200 bg-white/70 backdrop-blur-sm no-print">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between gap-3">
          <Link to="/sections" className="flex items-center gap-3" data-testid="brand-logo-home">
            <BrandLogo chip />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={savePdf}
              disabled={pdfBusy}
              data-testid="cheat-sheet-pdf-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0f766e] text-white px-3.5 py-1.5 text-xs font-semibold hover:bg-[#0e6b64] disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {pdfBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              {pdfBusy ? "Building…" : "Download PDF"}
            </button>
            <button
              type="button"
              onClick={() => window.print()}
              data-testid="cheat-sheet-print-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              <Printer className="w-3.5 h-3.5" /> Print
            </button>
            <button
              type="button"
              onClick={() => navigate("/cheat-sheets")}
              data-testid="cheat-sheet-back"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-[#0047AB]"
            >
              <ArrowLeft className="w-4 h-4" /> All sheets
            </button>
          </div>
        </div>
      </header>

      {/* Print-instructions banner — visible only on screen so the user knows to
          pick A3 landscape in the print dialog. Some browsers respect @page,
          others ignore it (Safari). */}
      <div className="no-print bg-teal-50/70 border-b border-teal-100">
        <div className="max-w-[1400px] mx-auto px-6 py-2.5 flex items-start gap-2 text-[13px] text-teal-900">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <strong>Download PDF</strong> for a proper A3-landscape file you can email/share, or use
            <strong> Print</strong> and pick "Save as PDF" as the destination. Physical print settings:
            A3 · Landscape · Margins: minimum (or 8 mm) · Scale: 100% · Background graphics: on.
          </div>
        </div>
      </div>

      {/* The main A3 print area. All content that must appear on the printed
          page lives inside .print-area (global visibility isolation). */}
      <main
        ref={areaRef}
        className="cs-a3 print-area max-w-[1400px] mx-auto px-6 py-6 bg-white"
        data-testid={`cheat-sheet-${sheet.id}`}
      >
        {/* Header strip — compact so it doesn't eat print real estate */}
        <div className="flex items-start gap-3 pb-3 border-b-2" style={{ borderColor: sheet.accent }}>
          <div
            className="w-10 h-10 rounded-md text-white flex items-center justify-center shrink-0"
            style={{ background: sheet.accent }}
          >
            <Icon className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p
              className="text-[10px] font-semibold uppercase tracking-[0.18em]"
              style={{ color: sheet.accent }}
            >
              Sheet {String(sheet.n).padStart(2, "0")} · A3 crib sheet · Cats + Dogs
            </p>
            <h1 className="mt-0.5 text-xl font-bold text-slate-900 leading-tight">
              {sheet.title}
            </h1>
            <p className="mt-0.5 text-[12px] text-slate-600 leading-snug max-w-4xl">{sheet.blurb}</p>
          </div>
          <div className="text-right text-[10px] text-slate-500 leading-tight shrink-0">
            {reviewed && (
              <div className="inline-flex items-center gap-1 font-semibold">
                <CalendarDays className="w-3 h-3" />
                Reviewed {reviewed}
              </div>
            )}
            <div className="mt-0.5 opacity-80">Not a substitute for the product label.<br />Verify against patient weight + current bloods.</div>
          </div>
        </div>

        {/* Three-column CSS multi-column layout. `column-fill:balance` keeps
            columns roughly equal in height even when sections vary in size. */}
        <div
          className="cs-grid mt-4"
          style={{
            columnCount: 3,
            columnGap: "18px",
            columnFill: "balance",
          }}
        >
          {sheet.sections.map((sec, i) => (
            <A3Section key={i} sec={sec} accent={sheet.accent} idx={i} />
          ))}
        </div>

        {/* Footer strip */}
        <div className="mt-4 pt-2 border-t border-slate-200 flex items-center justify-between text-[9px] text-slate-500">
          <div>certivsims · {sheet.title} · v{sheet.lastReviewed || ""}</div>
          <div>Always confirm doses against the current product label + patient bloods.</div>
        </div>
      </main>
    </div>
  );
}

/* A single section — tables, note lists, callouts and charts in a compact,
   break-safe wrapper. */
function A3Section({ sec, accent, idx }) {
  const wrap = "cs-section mb-3 break-inside-avoid inline-block w-full";

  if (sec.type === "table") {
    return (
      <section className={wrap} data-testid={`cheat-a3-section-${idx}`}>
        <h2 className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {sec.heading}
        </h2>
        <table className="w-full text-[11px] border-collapse border border-slate-300">
          <thead>
            <tr className="bg-slate-100">
              {sec.columns.map((c, i) => (
                <th
                  key={i}
                  className="text-left px-1.5 py-0.5 font-semibold text-slate-800 border border-slate-300 text-[10px]"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sec.rows.map((row, ri) => (
              <tr key={ri} className={ri % 2 === 1 ? "bg-slate-50/60" : ""}>
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-1.5 py-0.5 border border-slate-300 align-top ${
                      ci === 0 ? "font-semibold text-slate-900" : "text-slate-700"
                    }`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {sec.foot && <p className="cs-foot mt-1 text-[9.5px] text-slate-500 italic leading-tight">{sec.foot}</p>}
      </section>
    );
  }

  if (sec.type === "notes") {
    return (
      <section className={wrap} data-testid={`cheat-a3-section-${idx}`}>
        <h2 className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {sec.heading}
        </h2>
        <ul className="border border-slate-300 rounded-sm p-1.5 bg-white">
          {sec.items.map((t, i) => (
            <li
              key={i}
              className="cs-note-item flex items-start gap-1.5 text-[10.5px] text-slate-800 leading-tight py-0.5"
            >
              <span
                className="mt-1 w-1 h-1 rounded-full shrink-0"
                style={{ background: accent }}
                aria-hidden
              />
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </section>
    );
  }

  if (sec.type === "callout") {
    return (
      <section className={wrap} data-testid={`cheat-a3-section-${idx}`}>
        <h2 className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {sec.heading}
        </h2>
        <div className="border border-amber-400 bg-amber-50 rounded-sm p-2 flex items-start gap-1.5 text-[10.5px] text-amber-900 leading-tight">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />
          <span>{sec.body}</span>
        </div>
      </section>
    );
  }

  if (sec.type === "chart") {
    return (
      <section className={wrap} data-testid={`cheat-a3-section-${idx}`}>
        <h2 className="text-[11px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
          {sec.heading}
        </h2>
        <div className="border border-slate-300 rounded-sm bg-white p-1.5">
          <DoseChart section={sec} />
        </div>
        {sec.foot && <p className="cs-foot mt-1 text-[9.5px] text-slate-500 italic leading-tight">{sec.foot}</p>}
      </section>
    );
  }

  return null;
}

/* Dose-vs-weight chart — inline SVG so it renders in both html2pdf (jsPDF)
   and the browser print engine identically. Each series is a shaded band
   between min and max dose per kg across the x-axis (weight in kg).
   Species usually differ, so we overlap two bands (dog / cat) with a legend. */
function DoseChart({ section }) {
  const {
    series = [],
    xMax = 40,
    yMax = 100,
    xTicks = [0, 10, 20, 30, 40],
    yTicks = [0, 25, 50, 75, 100],
    xLabel = "Weight (kg)",
    yLabel = "Dose",
  } = section;

  // Compact SVG scaled to fit a single column of the A3 grid — matches column
  // widths roughly and stays readable when the whole sheet is printed.
  const W = 260;
  const H = 130;
  const padL = 34;   // room for y-axis labels
  const padR = 6;
  const padT = 8;
  const padB = 22;   // room for x-axis labels + title

  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const xToPx = (kg) => padL + (kg / xMax) * plotW;
  const yToPx = (dose) => padT + plotH - (dose / yMax) * plotH;

  // Compute band polygons for each series
  const bandPath = (perKgMin, perKgMax) => {
    const p0 = `${xToPx(0)},${yToPx(0)}`;
    const p1 = `${xToPx(xMax)},${yToPx(perKgMax * xMax)}`;
    const p2 = `${xToPx(xMax)},${yToPx(perKgMin * xMax)}`;
    const p3 = `${xToPx(0)},${yToPx(0)}`;
    return `M ${p0} L ${p1} L ${p2} L ${p3} Z`;
  };
  const linePath = (perKg) =>
    `M ${xToPx(0)},${yToPx(0)} L ${xToPx(xMax)},${yToPx(perKg * xMax)}`;

  return (
    <div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        xmlns="http://www.w3.org/2000/svg"
        className="block w-full h-auto"
        style={{ maxHeight: 160 }}
      >
        {/* plot area background */}
        <rect x={padL} y={padT} width={plotW} height={plotH} fill="#fafbfc" stroke="#cbd5e1" strokeWidth="0.5" />

        {/* y-axis gridlines + labels */}
        {yTicks.map((t) => {
          const y = yToPx(t);
          return (
            <g key={`y${t}`}>
              <line x1={padL} y1={y} x2={W - padR} y2={y} stroke="#e2e8f0" strokeWidth="0.5" />
              <text
                x={padL - 3}
                y={y + 3}
                textAnchor="end"
                fontSize="6.5"
                fill="#475569"
                fontFamily="system-ui, sans-serif"
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* x-axis gridlines + labels */}
        {xTicks.map((t) => {
          const x = xToPx(t);
          return (
            <g key={`x${t}`}>
              <line x1={x} y1={padT} x2={x} y2={padT + plotH} stroke="#e2e8f0" strokeWidth="0.5" />
              <text
                x={x}
                y={padT + plotH + 8}
                textAnchor="middle"
                fontSize="6.5"
                fill="#475569"
                fontFamily="system-ui, sans-serif"
              >
                {t}
              </text>
            </g>
          );
        })}

        {/* dose bands per series */}
        {series.map((s, i) => (
          <g key={i}>
            <path d={bandPath(s.min, s.max)} fill={s.color} fillOpacity="0.18" />
            <path d={linePath(s.min)} stroke={s.color} strokeWidth="0.8" strokeDasharray="2 2" fill="none" />
            <path d={linePath(s.max)} stroke={s.color} strokeWidth="1.1" fill="none" />
          </g>
        ))}

        {/* axis labels */}
        <text
          x={padL + plotW / 2}
          y={H - 3}
          textAnchor="middle"
          fontSize="7"
          fill="#334155"
          fontFamily="system-ui, sans-serif"
          fontWeight="600"
        >
          {xLabel}
        </text>
        <text
          x={-padT - plotH / 2}
          y={9}
          transform="rotate(-90)"
          textAnchor="middle"
          fontSize="7"
          fill="#334155"
          fontFamily="system-ui, sans-serif"
          fontWeight="600"
        >
          {yLabel}
        </text>
      </svg>

      {/* legend below the chart */}
      <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[8.5px] leading-tight">
        {series.map((s, i) => (
          <div key={i} className="inline-flex items-center gap-1">
            <span
              className="inline-block w-2 h-2 rounded-sm"
              style={{ background: s.color, opacity: 0.65 }}
            />
            <span className="text-slate-700">{s.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
function formatReviewed(ym) {
  if (typeof ym !== "string") return null;
  const m = ym.match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  return `${MONTHS[parseInt(m[2], 10) - 1]} ${m[1]}`;
}
